/* eslint-disable @typescript-eslint/camelcase */
import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import capitalize from 'lodash/capitalize';
import { useEscapeKey, usePreviousValue } from '@fenderdigital/custom-hooks';
import Spinner from '@fenderdigital/ui/Spinner';
import Heading from '@fenderdigital/ui/Heading';
import Spacer from '@fenderdigital/ui/Spacer';
import BodyText from '@fenderdigital/ui/BodyText';
import isDomUsable from '@fenderdigital/utils/dom';
import usePlayUser from '@fenderdigital/custom-hooks/hooks/usePlayUser';
import { useSelector } from 'react-redux';
import LessonNav from './LessonNav';
import { APP_BAR_HEIGHT } from '../../../components/SubscribedLayout/SubscribedLayoutContent';
import { PERSISTENT_UPGRADE_BANNER_HEIGHT } from '../../../components/PersistentUpgradeHeader/persistent-upgrade-styles';
import LessonVideo from './LessonVideo';
import LessonVideoDeprecated from './LessonVideoDeprecated';
import ResizeHandler from '../../../components/ResizeHandler';
import LessonTabModal from './LessonTabModal';
import BackButton from '../../../components/BackButton';
import EllipsisDropdown from '../../../components/EllipsisDropdown';
import MarkComplete from '../../../components/MarkComplete';
import { getArtistNames } from '../../../components/ProgressList/progress-list-utils';
import HeadTitle from '../../../components/Head/HeadTitle';
import {
  canReposition as canRepositionUtils,
  findLessonInFlatLessons,
  getContentOffset,
  getNavItems,
  getVideoTrackData,
  isLandscape,
  isDesktop,
  preventBodyScrollIfModalOpening,
  tabModalEventTrack,
  isTabletLandscape,
} from './lesson-utils';
import useDataLoader from '../../../utils/useDataLoader';
import { getLessonsPayload } from '../../../utils/progress-utils';
import { getTabLevelSettings } from '../../../lib/tab-data';
import {
  EventNames,
  trackEvent,
  trackPageEvents,
  siteId,
  trackEventAndUpdate,
  getAlgoliaTracking,
} from '../../../lib/analytics';
import packageJson from '../../../../package.json';
import segment from '../../../core/segment';
import useAd from '../../../hooks/useAd';
import useQueryParams from '../../../hooks/useQueryParams';
import { getAdSegmentData } from '../../../utils/segment-utils';
import { slugPageTitle } from '../../../utils/string-utils';
import { isLocked } from '../../../utils/access-utils';
import * as S from './Lesson-styled';
import {
  getActivityProperties,
  getCurriculumProperties,
  getNextActivityProperties,
  getUserActivitiesCompleted,
  isCourseCompleted,
} from '../../../utils/activity-utils';
import { isAtFirstRockGuitarLesson, isRockGuitarPath } from '../../../utils/path-utils';
import { getFlag } from '../../../lib/feature-flags';
import UserAgent from '../../../lib/user-agent';
import constructFeedbackMailToBody from '../../../utils/email-utils';

// 0 = show 0% of time, 50 = show 50% of time
const AD_FREQUENCY_PERCENTAGE = 0;

const titleEventMap = {
  Chords: EventNames.LESSON_CHORDS_CLICKED,
  Feedback: EventNames.LESSON_FEEDBACK_CLICKED,
  Info: EventNames.LESSON_INFO_CLICKED,
  Related: EventNames.LESSON_RELATED_CLICKED,
  Tabs: EventNames.LESSON_TABLATURE_CLICKED,
  Tone: EventNames.LESSON_TONE_CLICKED,
  Tools: EventNames.LESSON_TOOLS_CLICKED,
};

const Lesson = ({
  collection,
  currentCourse,
  currentLesson,
  relatedItems,
  nextCourse,
  nextLesson,
  currentPath,
  currentPlaylist,
  error,
  flags,
  history,
  myPath,
  updateCourseProgress,
  updateLessonStandaloneProgress,
  updatePathProgress,
  currentLessonProgress,
  updateLessonCompletedProgress,
  pathLessons,
  shouldQueueStreaksModal,
  updateLessonProgress,
  updateStreakVideos,
  nextURL,
  segmentPath,
  courseSlug,
}) => {
  const selector = state => ({
    myProgress: state.myProgress,
    paths: state.paths.watching,
    playlists: state.myPath.playlists,
    userInstrument: state.myPath.instrument,
    style: state.myPath.style,
  });
  const { paths, myProgress, playlists, userInstrument, style } = useSelector(selector);

  const [isRepositioned, setIsRepositioned] = useState(false);
  const [canReposition, setCanReposition] = useState(false);
  const [content, setContent] = useState([]);
  const [contentOffset, setContentOffset] = useState(getContentOffset(false));
  const [currentTab, setCurrentTab] = useState(0);
  const [isMinHeight, setIsMinHeight] = useState(false);
  const [tablatureNavIndex, setTablatureNavIndex] = useState(null);
  const [isTabFullScreen, setIsTabFullScreen] = useState(false);
  const [courseCompleted, setCourseCompleted] = useState(false);
  const [nextActivityProperties, setNextActivityProperties] = useState({});
  const [activityProperties, setActivityProperties] = useState({});
  const [curriculumProperties, setCurriculumProperties] = useState({});
  const [userActivitiesCompleted, setUserActivitiesCompleted] = useState({});
  const { isFetching } = useDataLoader();
  const { collectionSlug } = useParams();
  const { showAd } = useAd({
    adFrequencyPercentage: getFlag('useAds') ? 100 : AD_FREQUENCY_PERCENTAGE,
    isLoading: isFetching,
  });
  const { isFreeUser, accessLevel: userAccessLevel, user } = usePlayUser();
  const tabWasFullScreen = usePreviousValue(isTabFullScreen);
  const modalRef = useEscapeKey(() => setIsTabFullScreen(false));
  const params = useQueryParams();
  const isFirstRockLesson =
    !isFetching &&
    isRockGuitarPath(userInstrument, style) &&
    isAtFirstRockGuitarLesson(playlists, userInstrument);
  const userID = user?.userID;
  const autoplay = params?.get('autoplay') === 'true';
  const suppressAd = isFirstRockLesson || autoplay;
  const slugTitle = slugPageTitle(courseSlug);
  const pageTitle = `Fender Play Online Guitar Lessons - Learn How to Play Guitar - ${slugTitle}`;
  const lessonAccessLevel = currentLesson?.['access-levels'];
  const isPlay5 = getFlag('isPlay5');
  const isAvailable = !(isPlay5 && isLocked(lessonAccessLevel, userAccessLevel));
  const globalProperties = {
    fc_id: userID,
    user_id: userID,
  };
  const activityType = 'lesson-video';
  const isOnPath = myPath?.inProgress;
  const trackingActivityProperties = {
    ...globalProperties,
    ...activityProperties,
    activity_type: activityType,
  };
  const trackActivityCompleted = () => {
    trackEventAndUpdate(
      userID,
      EventNames.ACTIVITY_COMPLETED,
      {
        ...trackingActivityProperties,
        ...nextActivityProperties,
        is_on_path: isOnPath,
        ...getAlgoliaTracking('conversion', currentLesson?.id),
      },
      userActivitiesCompleted,
    );
  };
  const isMobileOrTablet = useMemo(() => UserAgent.isMobileOrTabletUAString(), []);
  const mailToSubject = encodeURIComponent('Play Lesson Feedback');
  const mailToBody = useMemo(
    () => constructFeedbackMailToBody(currentLesson, userID, isMobileOrTablet),
    [currentLesson, userID, isMobileOrTablet],
  );
  const mailToUrl = `mailto:playfeedback@fender.com?subject=${mailToSubject}&body=${mailToBody}`;

  useEffect(() => {
    if (error) history.push('/');
    if (!isFetching) {
      setAppState(); // eslint-disable-line
      trackLessonProgress(); // eslint-disable-line
      const adVariant = params?.get('ad_variant');
      const forceShowAd = params?.get('show_ad') === 'true';
      const analyticsData = getAdSegmentData({
        currentCourse,
        currentLesson,
        pathData: currentPath,
        currentPlaylist,
        adFrequencyPercentage: AD_FREQUENCY_PERCENTAGE,
      });

      !isFetching && (!suppressAd || forceShowAd) && showAd(analyticsData, adVariant, forceShowAd);

      const courseProgressStatus = isCourseCompleted(myProgress, myPath);
      const activityPropertiesEvent = getActivityProperties(currentLesson, currentPath?.style);
      const curriculumPropertiesEvent = getCurriculumProperties(paths);
      const userActivitiesCompletedEvent = getUserActivitiesCompleted(myProgress, myPath);
      const nextActivityEvent = getNextActivityProperties(nextLesson);

      setActivityProperties(activityPropertiesEvent);
      setCurriculumProperties(curriculumPropertiesEvent);
      setUserActivitiesCompleted(userActivitiesCompletedEvent);
      setCourseCompleted(courseProgressStatus);
      setNextActivityProperties(nextActivityEvent);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetching, error]);

  useEffect(() => {
    if (tabWasFullScreen && !isTabFullScreen) {
      const fullScreenButton = document.querySelector('button[data-id="expand-button"]');
      fullScreenButton.focus();
    }
  }, [tabWasFullScreen, isTabFullScreen]);

  useEffect(() => {
    currentLesson &&
      trackPageEvents(EventNames.LESSON_DETAIL, {
        lesson_id: currentLesson?.id,
        lesson_slug: currentLesson?.slug,
        lesson_type_1: currentLesson?.type,
        lesson_name: currentLesson?.title,
        fc_id: userID,
        ...getAlgoliaTracking('view', currentLesson?.id),
      });
  }, [currentLesson]);

  const activityDismissed = () => {
    trackEvent(EventNames.ACTIVITY_DISMISSED, {
      ...trackingActivityProperties,
    });
  };

  const onNavClick = title => {
    const navTitle = content[title].button.title;
    trackEvent(titleEventMap[navTitle], {
      fc_id: userID,
      lesson_id: currentLesson?.id,
      lesson_slug: currentLesson?.slug,
      lesson_type_1: currentLesson?.type,
      lesson_name: currentLesson?.title,
    });
    if (navTitle === 'Feedback' && isDomUsable()) {
      window.location.href = mailToUrl;
    } else {
      setCurrentTab(title);
    }
  };

  const onMinHeight = minHeight => {
    if (!isRepositioned) setIsMinHeight(minHeight);
  };

  const onResize = width => {
    const withRepositionCheck = canRepositionUtils(width);
    const newContentOffset = getContentOffset();
    setContentOffset(!withRepositionCheck ? newContentOffset : 0);
    setCanReposition(withRepositionCheck);
    setIsRepositioned(withRepositionCheck && isRepositioned);
  };

  const handleWatchedVideoProgress = (percentage, seconds) => {
    const { timeZone, locale } = Intl.DateTimeFormat().resolvedOptions();
    const properties = {
      fc_id: userID,
      lesson_id: currentLesson?.id,
      lesson: currentLesson?.title,
      lessonType: currentLesson?.type,
      lesson_slug: currentLesson?.slug,
      lesson_type_1: currentLesson?.type,
      lesson_name: currentLesson?.title,
      path: capitalize(currentPath?.style ?? ''),
      watchedTo: percentage,
      watched_to_percentage: percentage,
      watched_to_seconds: seconds,
      playlist: currentPlaylist?.['description-long'],
      course_id: currentCourse?.id,
      course_slug: currentCourse?.slug,
      course_name: currentCourse?.title,
      instrument: currentLesson?.instrument,
      playlist_id: currentPlaylist?.id,
      level: currentLesson?.level,
      path_id: currentPath?.id,
      genre: currentPath?.style,
      lessonTitle: currentLesson?.title,
      timeZone,
      locale,
    };

    const legacyProperties = {
      fcId: userID,
      siteId,
      course: currentCourse?.title,
    };

    if (collectionSlug && collectionSlug === collection?.slug) {
      const { id: collectionId, title: collectionTitle } = collection;
      properties.collection_id = collectionId;
      properties.collection_name = collectionTitle;
    }

    trackEvent(EventNames.WATCHED_VIDEO, properties, legacyProperties, false, true);

    switch (currentLesson?.type) {
      case 'song':
        percentage >= 95 && trackActivityCompleted();
        break;
      case 'exercise':
        percentage >= 90 && trackActivityCompleted();
        break;
      case 'basics':
      case 'chord':
      case 'riff':
      case 'skill':
      case 'technique':
      case 'theory':
      case 'tone':
        percentage === 75 && trackActivityCompleted();
        break;
      default:
        break;
    }
  };

  const getVideoProps = isDynamic => {
    const trackData = getVideoTrackData(currentLesson, userID);
    const { videoID } = trackData;
    let videoProps = {
      lesson: currentLesson,
      currentCourse,
      nextCourse,
      nextLesson,
      trackData,
      videoID,
    };
    if (isDynamic) {
      const className = isMinHeight ? 'ml0' : 'center h-100';
      const fullScreenStyles = { maxHeight: '46vh', minHeight: '250px' };
      videoProps = { ...videoProps, className, fullScreenStyles };
    }
    return videoProps;
  };

  const toggleTabModal = () => {
    const isModalOpening = !isTabFullScreen;
    tabModalEventTrack(currentLesson.title, isModalOpening);
    preventBodyScrollIfModalOpening(isModalOpening);
    setIsTabFullScreen(!isTabFullScreen);
    trackEvent(isTabFullScreen ? EventNames.TABLATURE_COLLAPSED : EventNames.TABLATURE_EXPANDED, {
      fc_id: userID,
      lesson_id: currentLesson?.id,
      lesson_slug: currentLesson?.slug,
      lesson_type_1: currentLesson?.type,
      lesson_name: currentLesson?.title,
    });
  };

  const setAppState = () => {
    const newContent = getNavItems(
      currentLesson,
      relatedItems,
      currentCourse,
      flags,
      toggleTabModal,
    );
    const tabIndex = newContent.findIndex(({ button }) => button.title === 'Tabs');
    const newCurrentTab = tabIndex > -1 ? tabIndex : 0;
    setContent(newContent);
    setTablatureNavIndex(tabIndex);
    setCurrentTab(newCurrentTab);
  };

  const trackLessonProgress = () => {
    if (!currentLesson) return null;
    const { id, instrument, type } = currentLesson;
    const { flatLessons, id: pathID, style } = myPath || {};
    const foundFlatLesson = findLessonInFlatLessons(currentLesson, flatLessons);
    if (foundFlatLesson) {
      const { playlistID, courseID, courseType } = foundFlatLesson;
      return updatePathProgress(
        pathID,
        playlistID,
        courseID,
        id,
        style,
        instrument,
        courseType,
        type,
        'lesson',
      );
    }
    if (currentCourse) {
      const { id: courseID, type: courseType } = currentCourse;
      return updateCourseProgress(courseID, id, courseType, type, instrument);
    }
    return updateLessonStandaloneProgress(id, type, 'lesson');
  };

  const renderDropdownMenu = () => {
    const isComplete = currentLessonProgress && currentLessonProgress?.completed;
    const lessons = getLessonsPayload([currentLesson], !isComplete);

    const trackingProperties = {
      fc_id: userID,
      lesson_id: currentLesson?.id,
      lesson_slug: currentLesson?.slug,
      lesson_type: currentLesson?.type,
      lesson_name: currentLesson?.title,
      course_id: currentCourse?.id,
      course_slug: currentCourse?.slug,
      course_name: currentCourse?.title,
      path: capitalize(currentPath?.style ?? ''),
      instrument: currentLesson?.instrument,
      playlist_id: currentPlaylist?.id,
      playlist: currentPlaylist?.['description-long'],
      level: currentLesson?.level,
      path_id: currentPath?.id,
      genre: currentPath?.style,
      is_on_path: !!myPath?.instrument && !!currentPath?.instrument,
      video_id: currentLesson?.['activity-lesson-video']?.id,
    };

    const legacyProperties = {
      user_id: userID,
    };

    const onMarkedComplete = () => {
      trackEvent(
        isComplete ? EventNames.LESSON_UNMARKED_AS_COMPLETE : EventNames.LESSON_MARKED_AS_COMPLETE,
        trackingProperties,
        legacyProperties,
      );

      updateLessonCompletedProgress(lessons);
    };

    const favoriteData = {
      slug: currentLesson?.slug,
      name: currentLesson?.title,
      type: currentLesson?.type,
    };

    return (
      <S.EllipsisDropdownContainer>
        <EllipsisDropdown
          favoritesId={currentLesson?.id}
          activityType="lesson"
          sourcePath="lesson-detail"
          favoriteData={favoriteData}
        >
          <MarkComplete isComplete={isComplete} isCourse={false} onConfirm={onMarkedComplete} />
        </EllipsisDropdown>
      </S.EllipsisDropdownContainer>
    );
  };

  const renderLessonTitle = artists => {
    const { title } = currentLesson;
    return (
      <S.LessonTitleContent>
        <S.LessonTitleContainer>
          <Spacer spacing="md1" />
          <Heading textSize={['f4', 'f7']} dataId="lesson-page-title">
            {title}
          </Heading>
          {getArtistNames(artists, { weight: 'bold' })}
        </S.LessonTitleContainer>
        {renderDropdownMenu()}
      </S.LessonTitleContent>
    );
  };

  const renderLessonMeta = (layoutArr, artists) => {
    const navItems = layoutArr.map(navItem => navItem.button);
    return (
      <S.LessonMetaContainer>
        <S.LessonTitleAndButtonsContainer>
          <BackButton onClick={activityDismissed} />
          {renderLessonTitle(artists)}
        </S.LessonTitleAndButtonsContainer>
        <LessonNav navItems={navItems} onClick={onNavClick} selected={currentTab} />
      </S.LessonMetaContainer>
    );
  };

  const renderLessonVideo = isDynamic => {
    const { videoWatchedSeconds } = currentLessonProgress || {};
    const videoProps = getVideoProps(isDynamic);
    return (
      <>
        {getFlag('isNewVideoPlayer') ? (
          <LessonVideo
            collection={collection}
            course={currentCourse}
            lesson={videoProps.lesson}
            path={currentPath}
            playlist={currentPlaylist}
          />
        ) : (
          <LessonVideoDeprecated
            {...videoProps}
            autoplay={autoplay}
            nextURL={nextURL}
            history={history}
            canReposition={canReposition}
            isRepositioned={isRepositioned}
            startAtTime={videoWatchedSeconds}
            updateLessonProgress={updateLessonProgress}
            updateStreakVideos={updateStreakVideos}
            shouldQueueStreaksModal={shouldQueueStreaksModal}
            isTabFullScreen={isTabFullScreen}
            onWatchedVideoProgress={handleWatchedVideoProgress}
            onVideoReposition={value => setIsRepositioned(value)}
            trackingActivityProperties={trackingActivityProperties}
            curriculumProperties={curriculumProperties}
            userActivitiesCompleted={userActivitiesCompleted}
            currentCourse={currentCourse}
            courseCompleted={courseCompleted}
            userID={userID}
          />
        )}
      </>
    );
  };

  const renderLesson = layoutArr => {
    if (!isAvailable) return history.replace('/plans/upgrade');
    const lessonVideo = currentLesson?.['activity-lesson-video'] || {};
    const { artists } = lessonVideo || {};
    const lessonMeta = renderLessonMeta(layoutArr, artists);
    const videoContainerClasses = isLandscape() ? '' : 'center fixed bg-black z-2 w-100';
    const heightStyles = isRepositioned ? '' : '47vh';
    const shouldFixButton = isLandscape() || isRepositioned ? false : isMinHeight;

    return (
      <ResizeHandler onResize={onResize}>
        <div className="lesson">
          {!isLandscape() && (
            <div
              className="w-100 ma0"
              style={{ height: isDesktop() || isTabletLandscape() ? heightStyles : contentOffset }}
            />
          )}
          <div
            className={videoContainerClasses}
            style={{
              top:
                isFreeUser && !isPlay5
                  ? `calc(${PERSISTENT_UPGRADE_BANNER_HEIGHT} + ${APP_BAR_HEIGHT})`
                  : APP_BAR_HEIGHT,
            }}
          >
            {renderLessonVideo()}
          </div>
          {lessonMeta}
        </div>
      </ResizeHandler>
    );
  };

  const renderFullScreenTabModal = () => {
    const { title, tabs, level, instrument } = currentLesson;
    const { artists } = currentLesson['activity-lesson-video'];
    const settings = getTabLevelSettings(level, instrument);
    return (
      <LessonTabModal
        ref={modalRef}
        artists={artists}
        settings={settings}
        title={title}
        onCloseClick={toggleTabModal}
        tabs={tabs}
      />
    );
  };

  const renderNavContent = () => {
    const currentTabContent = content[currentTab] ? content[currentTab].content : null;
    return currentTabContent;
  };

  if (isFetching) return <Spinner fullPage />;

  return (
    <div className="lesson">
      <HeadTitle title={pageTitle} />
      <div className="db">{renderLesson(content)}</div>
      <div className="ph4-l pv4 ph3">{renderNavContent()}</div>
      {isTabFullScreen && renderFullScreenTabModal()}
    </div>
  );
};

Lesson.propTypes = {
  history: PropTypes.shape({ push: PropTypes.func.isRequired }).isRequired,
  collection: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
  }),
  currentCourse: PropTypes.shape({}),
  currentLesson: PropTypes.shape({}),
  currentLessonProgress: PropTypes.shape({}),
  currentPath: PropTypes.shape({}),
  currentPlaylist: PropTypes.shape({
    'description-long': PropTypes.string,
  }),
  error: PropTypes.string,
  myPath: PropTypes.shape({}),
  flags: PropTypes.shape({}),
  nextCourse: PropTypes.shape({}),
  nextLesson: PropTypes.shape({}),
  relatedItems: PropTypes.shape({}),
  shouldQueueStreaksModal: PropTypes.func.isRequired,
  updateCourseProgress: PropTypes.func.isRequired,
  updateLessonProgress: PropTypes.func.isRequired,
  updateLessonCompletedProgress: PropTypes.func.isRequired,
  updateLessonStandaloneProgress: PropTypes.func.isRequired,
  updatePathProgress: PropTypes.func.isRequired,
  updateStreakVideos: PropTypes.func.isRequired,
  pathLessons: PropTypes.arrayOf(PropTypes.shape({})),
  nextURL: PropTypes.string.isRequired,
  segmentPath: PropTypes.shape({}).isRequired.isRequired,
};

Lesson.defaultProps = {
  collection: {},
  currentCourse: null,
  currentLesson: null,
  currentLessonProgress: null,
  currentPath: null,
  currentPlaylist: null,
  error: null,
  myPath: {},
  flags: null,
  nextCourse: null,
  nextLesson: null,
  relatedItems: null,
  pathLessons: [],
};

export default Lesson;
